import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth, type UserType } from "../../context/auth-context";
import { CHANGE_PASSWORD_PATH, loginHref } from "../../utils/next-path";
import { FullPageSpinner } from "../patterns/FullPageSpinner";

/**
 * Keeps a route behind a signed-in user of the right kind.
 *
 * This is navigation, not access control. The gateway and every service check
 * permissions independently; what this prevents is a user landing on a shell
 * that will only ever show them errors. Removing it would be a usability
 * regression, not a security hole — which is the right way round.
 *
 * The password-change bounce below is the one exception, and it is still not
 * the enforcement: the gateway refuses every request from an account that owes
 * a password change (`ProxyAuth.authenticate`), so a user who skipped this
 * redirect would reach a shell where nothing loads. This only makes that
 * refusal legible.
 *
 * @param props.userType - The audience this branch of the tree is for
 * @param props.children - What to render for a matching user
 * @returns The children, or a redirect
 */
export function RequireAuth({
    userType,
    children,
}: {
    userType: UserType;
    children: ReactNode;
}) {
    const { user, isLoading, isSigningOut } = useAuth();
    const location = useLocation();

    // The boot-time session restore has not settled. Rendering the login screen
    // here would flash it at every user on every reload — the session is very
    // often about to come back.
    if (isLoading) {
        return <FullPageSpinner />;
    }

    if (!user) {
        // A deliberate sign-out carries nothing. The page the last user was on
        // would otherwise sit in the URL while the next person signs in at the
        // same screen, and send them there — which is how a lecturer arrived on
        // a student record an administrator had open. Someone who signed out is
        // also, by definition, not trying to get anywhere.
        if (isSigningOut) {
            return <Navigate to="/login" replace />;
        }

        // Where they were going is carried along, so signing in returns them to
        // the link they followed instead of dropping them on a dashboard.
        //
        // In the URL as well as in router state: state survives a client-side
        // navigation but not a page load, and the common case here is someone
        // opening a bookmark or a link from an email — exactly the case with no
        // state to carry. `resolveNextPath` treats the parameter as untrusted,
        // and now also checks the destination belongs to whoever signs in.
        return (
            <Navigate
                to={loginHref(location.pathname + location.search)}
                replace
                state={{ from: location }}
            />
        );
    }

    // Nothing else in the platform is reachable until the issued password has
    // been replaced — see the note above. No exemption is needed for the
    // change-password screen itself: it sits outside the three shells, so this
    // guard never runs on it.
    if (user.mustChangePassword) {
        return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
    }

    if (user.userType !== userType) {
        // A guardian who follows a staff link is not an error to report; they
        // are simply somewhere that does not exist for them. Send them home.
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
}
